// Legal docs + SCC clickwrap API client — Phase 5 Plan 05-04.
//
// Phase 1 already exposes GET /api/v1/legal/{audience} via `consent.ts:getLegal`.
// This namespace expands for Phase 5 D-89:
//   - getDoc(docType, locale, version, format='md'|'pdf')
//   - getPdfUrl(docType, locale, version) — for external-browser openLink (ISSUE-07)
//   - acceptScc({company_id, scc_version, scc_hash, locale, signature_method})
//
// Notes per ISSUE-07 + Pitfall 11:
//   Telegram iOS WebView PDF rendering is unreliable. PDF download MUST open in
//   external browser via window.Telegram?.WebApp?.openLink — never embed.

import type { ApiClient } from "./client";

export type LegalDocType = "driver" | "dispatcher" | "customer" | "dpia" | "dpa" | "scc" | "lia";

export type LegalLocale = "en" | "tr" | "bg" | "ru" | "de" | "fr";

export interface LegalDocMarkdownResponse {
  audience: string;
  locale: string;
  version: string;
  markdown: string;
  sha256: string;
}

export interface SccAcceptRequest {
  company_id: number;
  scc_version: string;
  scc_hash: string;
  locale: string;
  signature_method?: "clickwrap";
}

export interface SccAcceptResponse {
  status: "accepted";
  company_id: number;
  version: string;
  sha256_hash: string;
  signed_at: string; // ISO 8601 UTC
}

/**
 * Build /api/v1/legal/{docType}?... URL with format choice.
 */
function buildLegalUrl(
  baseUrl: string,
  docType: LegalDocType,
  locale: LegalLocale,
  version: string,
  format: "md" | "pdf",
): string {
  const u = `${baseUrl}/api/v1/legal/${encodeURIComponent(docType)}`;
  const params = new URLSearchParams({
    locale,
    version,
    format,
  });
  return `${u}?${params.toString()}`;
}

export const legalApi = {
  /**
   * Fetch legal document markdown (JSON wrapper) — Phase 5 D-89.
   *
   * Returns JSON {markdown, sha256, ...}. Mini App renders markdown inline
   * via react-markdown (NO embedded PDF — ISSUE-07 fix).
   */
  async getDocMarkdown(
    baseUrl: string,
    docType: LegalDocType,
    locale: LegalLocale,
    version = "1.0",
  ): Promise<LegalDocMarkdownResponse> {
    const url = buildLegalUrl(baseUrl, docType, locale, version, "md");
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) {
      throw new Error(`getDocMarkdown failed: ${res.status} ${res.statusText}`);
    }
    return (await res.json()) as LegalDocMarkdownResponse;
  },

  /**
   * Build PDF URL for external-browser download (ISSUE-07).
   *
   * Caller MUST open via window.Telegram?.WebApp?.openLink(url) — NEVER embed
   * in <iframe> or <embed>; iOS WebView PDF rendering is unreliable.
   */
  getPdfUrl(baseUrl: string, docType: LegalDocType, locale: LegalLocale, version = "1.0"): string {
    return buildLegalUrl(baseUrl, docType, locale, version, "pdf");
  },

  /**
   * POST /api/v1/companies/scc-accept — clickwrap acceptance recording.
   *
   * Server validates current SCC text hash against `scc_hash` (anti-MITM).
   * Owner-only — apiClient injects Bearer JWT via interceptor.
   */
  async acceptScc(apiClient: ApiClient, body: SccAcceptRequest): Promise<SccAcceptResponse> {
    return apiClient.post<SccAcceptResponse>("/api/v1/companies/scc-accept", {
      ...body,
      signature_method: body.signature_method ?? "clickwrap",
    });
  },

  /**
   * Compute SHA-256 hex of UTF-8 markdown text — for client-side hash check
   * before POST (defence-in-depth; server still re-computes).
   *
   * Uses Web Crypto API which is available in Telegram WebView.
   */
  async sha256OfText(text: string): Promise<string> {
    const enc = new TextEncoder();
    const buf = await crypto.subtle.digest("SHA-256", enc.encode(text));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  },
};
